import React, { RefObject, ReactNode } from 'react';
import type { AREngine } from '../../utils/arEngine';

const SCENE_PROPS = {
    'color-space': 'sRGB',
    renderer: 'colorManagement: true, physicallyCorrectLights',
    'vr-mode-ui': 'enabled: false',
    'device-orientation-permission-ui': 'enabled: false',
} as const;

const MINDAR_IMAGE_OPTS =
    'smoothCount: 8; smoothTolerance: 0.005; missTolerance: 8; warmupTolerance: 5;';

export interface ARSceneProps {
    engine: AREngine;
    ready: boolean;
    mindTargetUrl: string;
    sceneRef: RefObject<HTMLElement>;
    children: ReactNode;
}

export const ARScene: React.FC<ARSceneProps> = ({
    engine,
    ready,
    mindTargetUrl,
    sceneRef,
    children,
}) => {
    if (engine === 'xr8' && !ready) return null;

    if (engine === 'xr8') {
        return (
            <a-scene
                ref={sceneRef}
                {...SCENE_PROPS}
                xrextras-loading
                xrextras-runtime-error
                xrweb="disableWorldTracking: true"
            >
                {children}
            </a-scene>
        );
    }

    return (
        <a-scene
            ref={sceneRef}
            {...SCENE_PROPS}
            mindar-image={`imageTargetSrc: ${mindTargetUrl}; ${MINDAR_IMAGE_OPTS}`}
        >
            {children}
        </a-scene>
    );
};

export interface ARImageTargetProps {
    engine: AREngine;
    targetName: string | null;
    children: ReactNode;
}

export const ARImageTarget: React.FC<ARImageTargetProps> = ({
    engine,
    targetName,
    children,
}) => {
    if (engine === 'xr8' && targetName) {
        return (
            <a-entity
                id="image_target"
                xrextras-named-image-target={`name: ${targetName}`}
            >
                {children}
            </a-entity>
        );
    }

    return (
        <a-entity
            id="image_target"
            mindar-image-target="targetIndex: 0; minScale: 0.98; maxScale: 1.02; scale: 1;"
        >
            {children}
        </a-entity>
    );
};
